import { ProgramsService } from "@/client"
import type { QuizzesListPublic } from "@/client/types.gen"
import {
  Alert,
  Box,
  Heading,
  SkeletonText,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"

interface ProgramQuizzesProps {
  programId: string
}

export function ProgramQuizzes({ programId }: ProgramQuizzesProps) {
  const { data, isLoading, isError, error } = useQuery<QuizzesListPublic>({
    queryKey: ["program-quizzes", programId],
    queryFn: async () => {
      const response = await ProgramsService.listProgramQuizzes({
        programId,
        skip: 0,
        limit: 100,
      })
      return response as QuizzesListPublic
    },
  })

  const quizzes = data?.data ?? []

  if (isLoading) {
    return (
      <Box
        borderWidth="1px"
        borderRadius="md"
        p={4}
        bg="white"
        data-testid="program-quizzes-panel"
      >
        <Heading size="md" mb={4}>
          Assigned Quizzes
        </Heading>
        <SkeletonText noOfLines={4} gap={3} />
      </Box>
    )
  }

  if (isError) {
    return (
      <Box
        borderWidth="1px"
        borderRadius="md"
        p={4}
        bg="white"
        data-testid="program-quizzes-panel"
      >
        <Heading size="md" mb={4}>
          Assigned Quizzes
        </Heading>
        <Alert.Root status="error">
          <Alert.Title>Unable to load quizzes</Alert.Title>
          <Alert.Description>
            {(error as Error).message || "Please try again later."}
          </Alert.Description>
        </Alert.Root>
      </Box>
    )
  }

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      p={4}
      bg="white"
      data-testid="program-quizzes-panel"
    >
      <Heading size="md" mb={4}>
        Assigned Quizzes ({data?.count ?? 0})
      </Heading>
      {quizzes.length === 0 ? (
        <Text color="gray.500">
          No quizzes have been assigned to this program yet.
        </Text>
      ) : (
        <VStack align="stretch" gap={3}>
          {quizzes.map((quiz) => (
            <Box
              key={quiz.id}
              borderWidth="1px"
              borderRadius="md"
              px={4}
              py={3}
              _hover={{ bg: "gray.50" }}
              data-testid="program-quiz-card"
              data-quiz-id={quiz.id}
            >
              <Link
                to="/quizzes/$quizId"
                params={{ quizId: quiz.id }}
                style={{ fontWeight: 600 }}
              >
                {quiz.name}
              </Link>
              <Text fontSize="sm" color="gray.600">
                {quiz.cutoff_date
                  ? `Due ${new Date(quiz.cutoff_date).toLocaleDateString()}`
                  : "No due date"}
              </Text>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  )
}
